import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, ChevronDown, ChevronRight, Globe, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { ModalOverlay } from "@/components/ModalOverlay";
import { PaginatedList } from "@/components/PaginatedList";
import { PartnerPicker } from "@/components/PartnerPicker";
import { usePartnerScope } from "@/components/partner-scope-context";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { usePaginatedList } from "@/lib/pagination";
import { canCreateAnySubPartner, canDeletePartner, isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import type { Partner, PartnerType } from "@tilt/admin-sdk";

// Per 2026-04-21 tenancy lock: the two live types are `platform` (the
// abstract root) and `partner` (everything under it, nestable). A
// pre-V0019 DB can still return legacy `white_label` / `customer_org`
// rows - `labelFor` treats them as partners at render time so the
// page doesn't crash before the cleanup migration applies.
function labelFor(t: string): string {
  return t === "platform" ? "Platform" : "Partner";
}

export function PartnersPage(): JSX.Element {
  usePageTitle("Partners");
  const queryClient = useQueryClient();
  const { scope } = usePartnerScope();
  const [createOpen, setCreateOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // The partner list is paginated server-side. ``partner_type=partner``
  // filters the Platform root out at the SQL level so the client
  // doesn't have to filter + sort before rendering. Search/ordering
  // live on the handler side too.
  const partnersList = usePaginatedList({
    queryKey: ["partners", scope.id, { showDeleted }],
    fetcher: ({ limit, offset, q }) =>
      apiClient
        .listPartners({
          limit,
          offset,
          ...(q ? { q } : {}),
          scope_path: scope.path,
          include_deleted: showDeleted,
          partner_type: "partner",
        })
        .then((res) => ({
          items: res.partners,
          total: res.total,
          limit: res.limit,
          offset: res.offset,
          has_more: res.has_more,
        })),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["partners"] });
  };

  // Gate the "New partner" button on the same rule the backend
  // enforces (partners.py). The display list above is scope-filtered
  // via ``scope_path`` - but the button should be visible whenever
  // the caller has *any* accessible partner with the flag flipped on,
  // not just within the current scope. Fire a separate unscoped
  // lookup with a generous limit so a caller whose only flag=true
  // partner lives outside the current scope still sees the CTA.
  // Separate queryKey from the scoped list so react-query doesn't
  // thrash between the two on scope switches. Shares a staleTime
  // with the scoped list, so navigation back and forth is cheap.
  const profile = useRoleProfile();
  const canCreateLookup = useQuery({
    queryKey: ["partners", "can-create-gate"],
    queryFn: () =>
      apiClient.listPartners({ limit: 500, partner_type: "partner", include_deleted: false }),
    staleTime: 60_000,
  });
  const canCreate = canCreateAnySubPartner(profile, canCreateLookup.data?.partners ?? []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Partners</h1>
          <p className="text-sm text-muted-foreground">
            Your partners and any sub-partners nested under them.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex flex-shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            New partner
          </button>
        )}
      </div>

      <PaginatedList
        state={partnersList}
        itemNoun="partners"
        searchPlaceholder="Search by name, slug, or path…"
        filters={
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              aria-label="Show deleted partners"
              className="h-3.5 w-3.5 accent-primary"
            />
            Show deleted
          </label>
        }
        emptyState={
          <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            No partners in scope. Create a new one to get started.
          </div>
        }
      >
        {partnersList.query.isError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {partnersList.query.error instanceof Error
              ? partnersList.query.error.message
              : "Failed to load partners"}
          </div>
        )}
        {partnersList.items.length > 0 && (
          <PartnersTable items={partnersList.items} invalidate={invalidate} />
        )}
      </PaginatedList>

      {createOpen && (
        <CreateDialog
          defaultParentId={scope.id}
          onClose={() => setCreateOpen(false)}
          onMutated={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

/** Sort so parents always come before their sub-partners and siblings
 *  group. ltree paths sort lexicographically into exactly this order
 *  (``platform.alpha``, ``platform.alpha.beta``, ``platform.alpha.gamma``,
 *  ``platform.delta``, …) so ``localeCompare`` on path is sufficient. */
function sortedForTree(items: readonly Partner[]): Partner[] {
  return items.slice().sort((a, b) => a.path.localeCompare(b.path));
}

/** Visible depth in the tree - chunks under the implicit ``platform``
 *  root. Top-level partners (``platform.foo``) → 0; one level of sub
 *  (``platform.foo.bar``) → 1. */
function treeDepth(path: string): number {
  const chunks = path.split(".");
  return Math.max(0, chunks.length - 2);
}

function PartnersTable({
  items,
  invalidate,
}: {
  readonly items: readonly Partner[];
  readonly invalidate: () => void;
}): JSX.Element {
  // Collapsed-by-default ids. Storing the COLLAPSED set (not the
  // expanded set) makes "default = expanded" the implicit fall-through
  // for any new row, which keeps freshly created sub-partners visible
  // without the operator having to expand their parent.
  //
  // Trade-off: a default-expanded tree leaves big trees noisy on
  // first render. We accept that because the alternative (default-
  // collapsed) hides every sub-partner unless the operator clicks
  // through every parent - worse for the common case where someone
  // visits this page to find a specific sub-partner.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const sorted = useMemo(() => sortedForTree(items), [items]);

  // Resolve which rows are visible given the current collapsed set.
  // A row is hidden when ANY ancestor (by path prefix) is collapsed.
  // Build a path → collapsed lookup once per render - O(P × log A)
  // where P is page rows and A is collapsed depth.
  const collapsedPaths = useMemo(() => {
    const out = new Set<string>();
    for (const id of collapsed) {
      const p = items.find((row) => row.id === id);
      if (p) out.add(p.path);
    }
    return out;
  }, [collapsed, items]);

  const visible = useMemo(() => {
    return sorted.filter((row) => {
      // Walk from the path's parent backward checking each ancestor.
      // Stop at "platform" (depth 1, the implicit root we never see).
      const chunks = row.path.split(".");
      for (let i = chunks.length - 1; i >= 2; i -= 1) {
        const ancestorPath = chunks.slice(0, i).join(".");
        if (collapsedPaths.has(ancestorPath)) return false;
      }
      return true;
    });
  }, [sorted, collapsedPaths]);

  // ``hasChildren`` decides whether a row gets a chevron toggle. We
  // prefer the server-supplied count when present (accurate across
  // pages); fall back to "any other row's path is prefixed by mine".
  const hasChildren = (row: Partner): boolean => {
    if ((row.sub_partner_count ?? 0) > 0) return true;
    const prefix = row.path + ".";
    return items.some((other) => other.path.startsWith(prefix));
  };

  const toggle = (id: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full text-sm">
        <thead className="sticky top-0 border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2.5">Name</th>
            <th className="px-4 py-2.5">Type</th>
            <th className="px-4 py-2.5">Path</th>
            <th className="px-4 py-2.5 text-right">Sub-partners</th>
            <th className="px-4 py-2.5 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((t) => (
            <PartnerRow
              key={t.id}
              partner={t}
              parent={items.find((p) => p.id === t.parent_id)}
              subCount={t.sub_partner_count ?? 0}
              depth={treeDepth(t.path)}
              hasChildren={hasChildren(t)}
              isCollapsed={collapsed.has(t.id)}
              onToggle={() => toggle(t.id)}
              onMutated={invalidate}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PartnerRow(props: {
  partner: Partner;
  parent: Partner | undefined;
  subCount: number;
  depth: number;
  hasChildren: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  onMutated: () => void;
}): JSX.Element {
  const { partner, parent, subCount, depth, hasChildren, isCollapsed, onToggle, onMutated } = props;
  const [error, setError] = useState<string | null>(null);
  const profile = useRoleProfile();
  // Delete mirrors partners._delete_partner. Revised matrix (2026-04-24):
  // super_admin bypasses; partner_admin can delete ONLY direct children
  // (parent row must be inside their write scope). Platform row always
  // hidden from delete. When parent isn't in the current page, we hide
  // the button - server still enforces, so this is just UX.
  const canDelete = canDeletePartner(profile, partner, parent ?? null);

  const softDelete = useMutation({
    mutationFn: () => apiClient.deletePartner(partner.id),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const typeClasses: Record<PartnerType, string> = {
    platform: "bg-primary/10 text-primary dark:bg-primary dark:text-primary-foreground",
    partner: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  };
  const classFor = (t: string): string =>
    t === "platform" ? typeClasses.platform : typeClasses.partner;
  const isPartnerLike = partner.partner_type !== "platform";
  const clickable = isPartnerLike && !partner.is_deleted;

  return (
    <>
      <tr className="border-b last:border-b-0 hover:bg-muted/30">
        <td className="px-4 py-2.5 align-middle">
          <span className="flex items-center">
            {/* Tree indent - one 18px stub per ancestor level. The
                final stub at depth>0 uses └ as a connector. The
                chevron toggle on parents takes the leaf slot when
                the row has children of its own (server-supplied
                ``sub_partner_count > 0`` OR any other in-page row's
                path is prefixed by this one). Toggle stays clickable
                even when collapsed; aria-expanded mirrors state. */}
            {depth > 0 && (
              <span
                aria-hidden="true"
                className="inline-flex items-center text-muted-foreground/50"
              >
                {Array.from({ length: depth - 1 }).map((_, i) => (
                  <span key={i} className="inline-block w-[18px]" />
                ))}
                <span className="inline-block w-[18px] text-base leading-none">└</span>
              </span>
            )}
            {hasChildren ? (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={!isCollapsed}
                aria-label={isCollapsed ? `Expand ${partner.name}` : `Collapse ${partner.name}`}
                className="mr-1 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </button>
            ) : (
              // Reserve the chevron slot so leaf-row names align with
              // their siblings that have a chevron.
              <span className="mr-1 inline-block h-5 w-5" aria-hidden="true" />
            )}
            {clickable ? (
              <Link
                to={`/partners/${partner.id}`}
                className="font-medium hover:text-primary hover:underline"
              >
                {partner.name}
              </Link>
            ) : (
              <span className="font-medium">{partner.name}</span>
            )}
            {partner.is_deleted && (
              <span className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
                deleted
              </span>
            )}
            {partner.custom_domain && (
              <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <Globe className="h-3 w-3" aria-hidden="true" />
                {partner.custom_domain}
              </span>
            )}
          </span>
        </td>
        <td className="px-4 py-2.5 align-middle">
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[10px] font-medium",
              classFor(partner.partner_type),
            )}
          >
            {labelFor(partner.partner_type)}
          </span>
        </td>
        <td className="break-all px-4 py-2.5 align-middle font-mono text-xs text-muted-foreground">
          {partner.path}
        </td>
        <td className="px-4 py-2.5 text-right align-middle tabular-nums text-muted-foreground">
          {isPartnerLike ? subCount : "-"}
        </td>
        <td className="px-4 py-2.5 align-middle">
          <div className="flex items-center justify-end gap-2">
            {!partner.is_deleted && partner.partner_type !== "platform" && canDelete && (
              <button
                type="button"
                disabled={softDelete.isPending}
                onClick={() => {
                  const noun = labelFor(partner.partner_type).toLowerCase();
                  if (!confirm(`Soft-delete ${noun} "${partner.name}"?`)) return;
                  softDelete.mutate();
                }}
                aria-label={`Delete ${labelFor(partner.partner_type).toLowerCase()} ${partner.name}`}
                title={`Delete ${labelFor(partner.partner_type).toLowerCase()} ${partner.name}`}
                className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            )}
          </div>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={5} className="px-4 py-2">
            <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
          </td>
        </tr>
      )}
    </>
  );
}

export function CreateDialog(props: {
  defaultParentId: string;
  onClose: () => void;
  onMutated: () => void;
}): JSX.Element {
  const profile = useRoleProfile();
  // Only Platform admins (super_admin) can grant
  // ``can_manage_sub_partners``. Hide the checkbox for everyone
  // else - the backend also rejects the flag on non-super POSTs,
  // so this is belt-and-suspenders. When hidden, the dialog always
  // submits ``can_manage_sub_partners: false`` for the new row.
  const canGrantSubPartnerFlag = isSuperAdmin(profile);
  const [parentId, setParentId] = useState(props.defaultParentId);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  // Post-2026-04-21 tenancy lock, the only thing created under a
  // platform or partner is another partner (sub-partner).
  // Customer organizations are created via /admin/v1/organizations,
  // not /admin/v1/partners.
  const partnerType = "partner" as const;
  const [customDomain, setCustomDomain] = useState("");
  // V0015 - per-partner opt-in for sub-partner self-serve. Default
  // FALSE so a partner admin can't spawn sub-partners unless a
  // platform admin explicitly flips it on (and only they can; the
  // checkbox below is hidden for non-super callers).
  const [canManageSubPartners, setCanManageSubPartners] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiClient.createPartner({
        parent_id: parentId,
        partner_type: partnerType,
        name: name.trim(),
        slug: slug.trim(),
        can_manage_sub_partners: canManageSubPartners,
        ...(customDomain.trim() ? { custom_domain: customDomain.trim() } : {}),
      }),
    onSuccess: () => {
      setError(null);
      props.onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  // Auto-derive slug from name the first time the user types in name.
  // Stop auto-deriving once they've manually edited the slug.
  const [slugManual, setSlugManual] = useState(false);
  const onNameChange = (value: string): void => {
    setName(value);
    if (!slugManual) {
      setSlug(
        value
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9-]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 30),
      );
    }
  };

  return (
    <ModalOverlay ariaLabel="Create partner" onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="w-full max-w-md space-y-3 rounded-t-xl border bg-card p-4 shadow-lg sm:rounded-xl sm:p-5"
      >
        <h2 className="text-sm font-semibold">Create partner</h2>

        <div>
          <label htmlFor="t-name" className="mb-1 block text-xs font-medium text-muted-foreground">
            Name
          </label>
          <input
            id="t-name"
            type="text"
            required
            autoComplete="off"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="Acme Corp"
          />
        </div>

        <div>
          <label htmlFor="t-slug" className="mb-1 block text-xs font-medium text-muted-foreground">
            Slug <span className="text-muted-foreground/80">(a-z, 0-9, hyphens; 3-30 chars)</span>
          </label>
          <input
            id="t-slug"
            type="text"
            required
            autoComplete="off"
            spellCheck={false}
            value={slug}
            onChange={(e) => {
              setSlugManual(true);
              setSlug(e.target.value);
            }}
            pattern="[a-z][a-z0-9-]{2,29}"
            className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
            placeholder="acme-corp"
          />
        </div>

        <div>
          <label
            htmlFor="t-parent"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Parent partner
          </label>
          <PartnerPicker
            id="t-parent"
            required
            value={parentId}
            onChange={setParentId}
            includePlatform
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Sub-partners are nested under the parent. To onboard a customer organization, use the
            Organizations page.
          </p>
        </div>

        <div>
          <label
            htmlFor="t-domain"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Custom domain <span className="text-muted-foreground/80">(optional)</span>
          </label>
          <input
            id="t-domain"
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={customDomain}
            onChange={(e) => setCustomDomain(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 font-mono text-xs"
            placeholder="portal.acme.com"
          />
        </div>

        {canGrantSubPartnerFlag && (
          <label className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-xs">
            <input
              type="checkbox"
              checked={canManageSubPartners}
              onChange={(e) => setCanManageSubPartners(e.target.checked)}
              aria-label="Allow this partner to manage sub-partners"
              className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-primary"
            />
            <span>
              <span className="block font-medium text-foreground">
                Allow this partner to manage sub-partners
              </span>
              <span className="mt-0.5 block text-muted-foreground">
                When on, this partner's admins can create their own sub-partners (resellers,
                affiliates) nested under them. Leave off if they should only manage organizations
                and locations.{" "}
                <span className="italic">
                  Platform-admin only - partner admins can't flip this.
                </span>
              </span>
            </span>
          </label>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={create.isPending || !name.trim() || !slug.trim() || !parentId}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Building2 className="h-4 w-4" />
            {create.isPending ? "Creating…" : "Create partner"}
          </button>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}
